import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { getArticleById, getRelatedArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      const value = height > 0 ? (window.scrollY / height) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, value)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useSeo({
    title: article
      ? `${article.title} - 18爆料网`
      : "内容不存在 - 18爆料网",
    description: article ? article.excerpt : "该内容可能已被移除或链接有误。",
    keywords: article ? `${article.tags.join(",")},18爆料网,${article.categoryName}` : "18爆料网",
    path: `/article/${id}`,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            dateModified: article.date,
            inLanguage: "zh-CN",
            articleSection: article.categoryName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "18爆料网编辑组" },
            publisher: { "@type": "Organization", name: "18爆料网" },
            image: article.cover,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://18baoliao.com/" },
              { "@type": "ListItem", position: 2, name: article.categoryName, item: "https://18baoliao.com/topics" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-4 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-500"></i>
        <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">
          没找到这篇内容
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          它可能已被移除，或者链接地址有误。回到首页看看最新的爆料吧。
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);
  const headings = article.body.filter((b) => b.type === "h");

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-[60] h-0.5 bg-transparent">
        <div className="h-full bg-primary-500 transition-[width]" style={{ width: `${progress}%` }} />
      </div>

      <article className="bg-background-50 pt-24 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "栏目", to: "/topics" },
              { label: article.categoryName, to: `/topics?c=${article.category}` },
              { label: "正文" },
            ]}
          />

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-price-tag-3-line"></i>
                {article.categoryName}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl md:leading-snug">
                {article.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  约 {article.readTime} 分钟
                </span>
              </div>

              <figure className="mt-6">
                <div className="h-56 w-full overflow-hidden rounded-lg md:h-96">
                  <img
                    src={article.cover}
                    alt={`${article.title} - ${article.categoryName}配图`}
                    title={`${article.title} - ${article.categoryName}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-foreground-400">
                  图 / 18爆料网编辑组整理
                </figcaption>
              </figure>

              <div className="mt-8">
                <p className="border-l-4 border-primary-500 bg-background-100 p-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                  {article.excerpt}
                </p>
              </div>

              <div className="mt-8 space-y-5">
                {article.body.map((block, idx) => {
                  if (block.type === "h") {
                    return (
                      <h2
                        key={idx}
                        className="pt-3 font-heading text-xl font-black text-foreground-950 md:text-2xl"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={idx}
                        className="rounded-lg border border-accent-200 bg-accent-50 p-5 font-heading text-base leading-8 text-accent-950 md:text-lg"
                      >
                        <i className="ri-double-quotes-l mr-1 text-xl text-accent-500"></i>
                        {block.text}
                      </blockquote>
                    );
                  }
                  return (
                    <p key={idx} className="text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/topics?c=${article.category}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    #{t}
                  </Link>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 text-xs leading-6 text-foreground-500">
                本文内容基于公开报道与平台热点整理，旨在呈现话题脉络。若涉及事实更正或权利问题，请联系
                hello@18baoliao.com，我们会尽快处理。
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                {headings.length > 0 && (
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
                    <ul className="mt-4 space-y-3">
                      {headings.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                          {h.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-links-line text-primary-500"></i>
                    相关内容
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {related.map((a) => (
                      <li key={a.id}>
                        <Link
                          to={`/article/${a.id}`}
                          className="block text-sm leading-6 text-foreground-700 transition-colors hover:text-primary-600"
                        >
                          {a.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-900 p-5">
                  <h2 className="font-heading text-base font-bold text-background-50">更多栏目</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/topics?c=${c.slug}`}
                        className="rounded-full border border-background-700 px-3 py-1 text-xs text-background-300 transition-colors hover:border-accent-400 hover:text-accent-400"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-16 border-t border-background-200 pt-10">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              继续吃瓜
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-6 pb-16 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}